import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Moon, Droplets, Calendar, Sparkles, Heart, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MoonCycleTracker = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("moon");
  const [moonData, setMoonData] = useState(null);
  const [cycleData, setCycleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogPeriod, setShowLogPeriod] = useState(false);
  const [periodDate, setPeriodDate] = useState(new Date().toISOString().split('T')[0]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  useEffect(() => {
    if (isOpen) {
      fetchMoonData();
      fetchCycleData();
    }
  }, [isOpen]);

  const fetchMoonData = async () => {
    try {
      const res = await axios.get(`${API}/moon/current`, getAuthHeader());
      setMoonData(res.data);
    } catch (err) {
      console.error("Error fetching moon data:", err);
    }
  };

  const fetchCycleData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cycle/current-phase`, getAuthHeader());
      setCycleData(res.data);
    } catch (err) {
      console.error("Error fetching cycle data:", err);
    } finally {
      setLoading(false);
    }
  };

  const logPeriod = async () => {
    try {
      const response = await axios.post(
        `${API}/cycle/log-period?start_date=${periodDate}`,
        null,  // Don't send a body for query-param-only endpoint
        getAuthHeader()
      );
      console.log("Period logged successfully:", response.data);
      toast.success("Period logged! 🌸");
      setShowLogPeriod(false);
      fetchCycleData();
    } catch (err) {
      console.error("Failed to log period:", err.response?.data || err.message);
      const errorMsg = err.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Failed to log period");
    }
  };

  const getMoonGradient = (phase) => {
    // Dark mode friendly gradients with better contrast
    const gradients = {
      new_moon: "from-slate-800 via-slate-900 to-slate-950",
      waxing_crescent: "from-slate-700 via-indigo-900 to-slate-800",
      first_quarter: "from-slate-600 via-violet-800 to-indigo-900",
      waxing_gibbous: "from-indigo-700 via-purple-700 to-violet-800",
      full_moon: "from-indigo-600 via-violet-600 to-purple-700",
      waning_gibbous: "from-violet-700 via-purple-800 to-indigo-800",
      last_quarter: "from-purple-800 via-slate-700 to-slate-800",
      waning_crescent: "from-indigo-900 via-slate-800 to-slate-900"
    };
    return gradients[phase] || gradients.new_moon;
  };

  const getCycleThemeColor = (phase) => {
    const colors = {
      menstrual: "from-red-500/20 via-rose-500/20 to-pink-500/20",
      follicular: "from-green-500/20 via-emerald-500/20 to-teal-500/20",
      ovulatory: "from-amber-500/20 via-orange-500/20 to-yellow-500/20",
      luteal: "from-purple-500/20 via-violet-500/20 to-indigo-500/20"
    };
    return colors[phase] || colors.menstrual;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0 pb-16">
        {/* Tab Headers */}
        <div className="flex border-b sticky top-0 bg-background z-10">
          <button
            onClick={() => setActiveTab("moon")}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-all ${
              activeTab === "moon" 
                ? "text-amber-600 border-b-2 border-amber-500 bg-amber-500/5" 
                : "text-muted-foreground hover:text-amber-600"
            }`}
          >
            <Moon className="w-4 h-4" />
            Moon Phase
          </button>
          <button
            onClick={() => setActiveTab("cycle")}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-all ${
              activeTab === "cycle" 
                ? "text-pink-600 border-b-2 border-pink-500 bg-pink-500/5" 
                : "text-muted-foreground hover:text-pink-600"
            }`}
          >
            <Droplets className="w-4 h-4" />
            My Cycle
          </button>
        </div>

        <div className="p-4">
          {/* MOON PHASE TAB */}
          {activeTab === "moon" && moonData && (
            <div className="space-y-4">
              {/* Moon Visual */}
              <div className={`p-6 rounded-2xl bg-gradient-to-br ${getMoonGradient(moonData.phase)} text-center relative overflow-hidden shadow-lg border border-white/10`}>
                <div className="absolute inset-0 opacity-30">
                  {[...Array(30)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 2}s`
                      }}
                    />
                  ))}
                </div>
                {/* Moon glow effect */}
                <div className="absolute inset-0 flex items-center justify-center opacity-20">
                  <div className="w-32 h-32 rounded-full bg-white blur-3xl" />
                </div>
                <span className="text-6xl block mb-2 relative z-10 drop-shadow-lg">{moonData.emoji}</span>
                <h3 className="text-xl font-bold text-white mb-1 relative z-10 drop-shadow-md">{moonData.name}</h3>
                <p className="text-white/90 text-sm relative z-10 drop-shadow-sm">{moonData.illumination}% illuminated</p>
                <p className="text-white/70 text-xs mt-1 relative z-10">Day {moonData.cycle_day} of lunar cycle</p>
              </div>

              {/* Theme & Energy */}
              {moonData.guidance && (
                <>
                  <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-amber-700 dark:text-amber-400">
                        {moonData.guidance.theme}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{moonData.guidance.energy}</p>
                  </div>

                  {/* Affirmation */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 text-center">
                    <p className="text-sm italic text-purple-700 dark:text-purple-400">
                      "{moonData.guidance.affirmation}"
                    </p>
                  </div>

                  {/* Rituals */}
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <span>🌙</span> Moon Rituals
                    </h4>
                    <div className="space-y-2">
                      {moonData.guidance.rituals?.slice(0, 4).map((ritual, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-amber-500">✦</span>
                          <span>{ritual}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Self Care */}
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <span>🌿</span> Self-Care for This Phase
                    </h4>
                    <div className="space-y-2">
                      {moonData.guidance.self_care?.slice(0, 3).map((care, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-green-500">♡</span>
                          <span>{care}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Crystals & Colors */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-1">Crystals</p>
                      <p className="text-sm font-medium">{moonData.guidance.crystals?.join(", ")}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-1">Colors</p>
                      <p className="text-sm font-medium">{moonData.guidance.colors?.join(", ")}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* CYCLE TAB */}
          {activeTab === "cycle" && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
                </div>
              ) : cycleData?.phase ? (
                <>
                  {/* Current Phase */}
                  <div className={`p-5 rounded-2xl bg-gradient-to-br ${getCycleThemeColor(cycleData.phase)} border border-pink-500/20 text-center`}>
                    <p className="text-xs text-muted-foreground mb-1">{cycleData.phase_info?.theme}</p>
                    <h3 className="text-xl font-bold mb-1">{cycleData.phase_info?.name}</h3>
                    <p className="text-sm text-muted-foreground">Day {cycleData.cycle_day} of your cycle</p>
                    
                    {/* Moon Alignment */}
                    {cycleData.moon_cycle_aligned && (
                      <div className="mt-3 p-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          {cycleData.alignment_message}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Phase Info */}
                  {cycleData.phase_info && (
                    <>
                      {/* Energy & Affirmation */}
                      <div className="p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/20">
                        <p className="text-xs text-muted-foreground mb-1">Energy</p>
                        <p className="font-medium text-pink-700 dark:text-pink-400 mb-2">
                          {cycleData.phase_info.energy}
                        </p>
                        <p className="text-sm italic">"{cycleData.phase_info.affirmation}"</p>
                      </div>

                      {/* Spiritual Insight */}
                      <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-violet-500/10 border border-purple-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <span>✨</span>
                          <span className="font-semibold text-sm">Spiritual Insight</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {cycleData.phase_info.spiritual_insight}
                        </p>
                      </div>

                      {/* Self-Care */}
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Heart className="w-4 h-4 text-pink-500" />
                          Self-Care Recommendations
                        </h4>
                        <div className="space-y-2">
                          {cycleData.phase_info.self_care?.slice(0, 4).map((care, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm">
                              <span className="text-pink-500">♡</span>
                              <span>{care}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Foods */}
                      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                        <p className="text-xs text-muted-foreground mb-1">🍃 Nourishing Foods</p>
                        <p className="text-sm">{cycleData.phase_info.foods?.join(", ")}</p>
                      </div>

                      {/* Avoid */}
                      {cycleData.phase_info.avoid && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                          <p className="text-xs text-muted-foreground mb-1">⚠️ Try to Avoid</p>
                          <p className="text-sm">{cycleData.phase_info.avoid?.join(", ")}</p>
                        </div>
                      )}

                      {/* Crystals */}
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground mb-1">💎 Supportive Crystals</p>
                        <p className="text-sm font-medium">{cycleData.phase_info.crystals?.join(", ")}</p>
                      </div>
                    </>
                  )}

                  {/* Next Period Prediction */}
                  {cycleData.next_period_prediction && (
                    <div className="p-4 rounded-xl bg-gradient-to-r from-rose-500/10 to-red-500/10 border border-rose-500/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">Next Period</p>
                          <p className="font-semibold">{cycleData.next_period_prediction.date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-rose-600">
                            {cycleData.next_period_prediction.days_until}
                          </p>
                          <p className="text-xs text-muted-foreground">days away</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Log Period Button */}
                  <Button 
                    onClick={() => setShowLogPeriod(true)}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                  >
                    <Droplets className="w-4 h-4 mr-2" />
                    Log Period Start
                  </Button>
                </>
              ) : (
                // No cycle data yet
                <div className="text-center py-8">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center">
                    <Droplets className="w-10 h-10 text-pink-500" />
                  </div>
                  <h3 className="font-semibold mb-2">Track Your Cycle</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Log your period to get personalized insights synced with the moon phases
                  </p>
                  <Button 
                    onClick={() => setShowLogPeriod(true)}
                    className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                  >
                    <Droplets className="w-4 h-4 mr-2" />
                    Log First Period
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Log Period Modal */}
        {showLogPeriod && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-xl p-5 max-w-sm w-full">
              <h3 className="font-semibold mb-4">Log Period Start</h3>
              <input
                type="date"
                value={periodDate}
                onChange={(e) => setPeriodDate(e.target.value)}
                className="w-full p-3 rounded-lg border bg-background mb-4"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowLogPeriod(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={logPeriod} className="flex-1 bg-pink-500 hover:bg-pink-600 text-white">
                  Log Period
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MoonCycleTracker;
